import * as React from "react";
import { Link, router, usePage } from "@inertiajs/react";
import {
  Sun,
  Moon,
  Monitor,
  LogOut,
  Check,
  FolderKanban,
  Key,
  User as UserIcon,
  Building2,
  Send,
  Globe,
  KeyRound,
  Radio,
  ScrollText,
  Settings,
  PanelLeftClose,
} from "lucide-react";
import { Avatar } from "@ui/Avatar";
import { Menu } from "@ui/Menu";
import { useTheme, type ThemeMode } from "../ThemeContext";
import { GlyphTile } from "./ProjectSwitcher";
import logoUrl from "../../images/lia_fetch.svg";
import type {
  SharedPageProps,
  User,
  Organization,
  Project,
} from "@lib/inertia-types";
import { cn } from "@lib/cn";

// Thin, Smartsheet-style left rail with a Cloverhound teal→blue gradient.
// Project context (the switcher) lives in the main panel's top bar; this rail
// carries the section nav plus the user menu. It paints its own gradient (not
// just the transparent root one) so it stays opaque when it floats over the
// card as a drawer on narrow screens.
export function RailNav({ onClose }: { onClose?: () => void }) {
  const { url, props } = usePage<SharedPageProps>();
  const currentOrg = props.current_organization ?? null;
  const currentProject = props.current_project ?? null;
  const user = props.auth?.user;
  const userOrganizations = props.organizations ?? [];

  // On narrow screens the rail is a drawer; tapping a nav link dismisses it.
  function handleNavClick(e: React.MouseEvent<HTMLElement>) {
    if (window.innerWidth >= 1024) return;
    if ((e.target as HTMLElement).closest("a")) onClose?.();
  }

  return (
    <aside
      className="flex h-full min-h-0 flex-col items-center gap-1 bg-linear-to-b from-[#00819c] from-60% to-cloverhound-blue-600 py-3 text-white"
      aria-label="Primary"
      onClick={handleNavClick}
    >
      <Link
        href="/projects"
        aria-label="All projects"
        className="relative -mt-1 mb-2 flex size-12 items-center justify-center transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
      >
        {/* White backing circle sits 2px smaller than the mascot. */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute left-1/2 top-1/2 -mt-1 size-10.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/25"
        />
        <img
          src={logoUrl}
          alt="Fetch"
          className="relative size-full scale-130 object-contain drop-shadow-sm"
        />
      </Link>

      <nav className="flex min-h-0 flex-1 flex-col items-center gap-1 self-stretch overflow-y-auto px-2">
        {currentProject ? (
          <ProjectRailNav project={currentProject} currentUrl={url} />
        ) : (
          <RailItem
            href="/projects"
            active={url === "/" || url.startsWith("/projects")}
            icon={FolderKanban}
            label="Projects"
          />
        )}
      </nav>

      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Hide sidebar"
          title="Hide sidebar"
          className="flex size-9 shrink-0 items-center justify-center rounded-lg text-white/70 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
        >
          <PanelLeftClose className="size-5" aria-hidden="true" />
        </button>
      )}

      {user && (
        <UserMenu
          user={user}
          currentOrg={currentOrg}
          userOrganizations={userOrganizations}
        />
      )}
    </aside>
  );
}

// ──────────────────────────────────────────────────────────────────────
// ProjectRailNav — section links for the current project
// ──────────────────────────────────────────────────────────────────────

function ProjectRailNav({
  project,
  currentUrl,
}: {
  project: Project;
  currentUrl: string;
}) {
  const base = `/projects/${project.slug}`;
  return (
    <>
      <RailItem
        href={base}
        active={currentUrl === base || currentUrl.startsWith(`${base}/requests`)}
        icon={Send}
        label="Requests"
      />
      <RailItem
        href={`${base}/environments`}
        active={currentUrl.startsWith(`${base}/environments`)}
        icon={Globe}
        label="Envs"
        title="Environments"
      />
      <RailItem
        href={`${base}/credentials`}
        active={currentUrl.startsWith(`${base}/credentials`)}
        icon={KeyRound}
        label="Creds"
        title="Credentials"
      />
      <RailItem
        href={`${base}/mocks`}
        active={currentUrl === `${base}/mocks`}
        icon={Radio}
        label="Mocks"
      />
      <RailItem
        href={`${base}/mocks/logs`}
        active={currentUrl.startsWith(`${base}/mocks/logs`)}
        icon={ScrollText}
        label="Logs"
      />
      <RailItem
        href={`${base}/settings`}
        active={currentUrl.startsWith(`${base}/settings`)}
        icon={Settings}
        label="Settings"
      />
    </>
  );
}

function RailItem({
  href,
  active,
  icon: Icon,
  label,
  title,
}: {
  href: string;
  active: boolean;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
  title?: string;
}) {
  return (
    <Link
      href={href}
      aria-current={active ? "page" : undefined}
      title={title ?? label}
      className={cn(
        "flex w-full flex-col items-center gap-1 rounded-lg px-1 py-2 transition-colors duration-quick ease-out-quad",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70",
        active
          ? "bg-white/20 text-white"
          : "text-white/75 hover:bg-white/10 hover:text-white",
      )}
    >
      <Icon className="size-5 shrink-0" aria-hidden="true" />
      <span className="text-center text-micro font-medium leading-tight">
        {label}
      </span>
    </Link>
  );
}

// ──────────────────────────────────────────────────────────────────────
// UserMenu — avatar trigger at the foot of the rail
// ──────────────────────────────────────────────────────────────────────

function UserMenu({
  user,
  currentOrg,
  userOrganizations,
}: {
  user: User;
  currentOrg: Organization | null;
  userOrganizations: Organization[];
}) {
  const { mode, setMode, isDark } = useTheme();

  const handleLogout = () => {
    router.delete("/session", { preserveScroll: true });
  };

  const displayEmail = user.email_address ?? "";
  const displayName = user.name ?? displayEmail;
  const ThemeIcon = mode === "system" ? Monitor : isDark ? Moon : Sun;

  return (
    <div className="mt-1 shrink-0">
      <Menu.Root>
        <Menu.Trigger
          className={cn(
            "flex items-center justify-center rounded-full p-0.5 ring-2 ring-white/40 transition-shadow",
            "hover:ring-white/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white",
          )}
          aria-label="User menu"
        >
          <Avatar
            size="lg"
            alt={displayName || "?"}
            src={user.avatar_url ?? undefined}
          />
        </Menu.Trigger>
        <Menu.Content
          align="end"
          side="right"
          sideOffset={10}
          className="min-w-[16rem]"
        >
          <div className="px-2 py-1.5">
            <p className="truncate text-small font-semibold text-cloverhound-gray-900 dark:text-cloverhound-gray-50">
              {displayName}
            </p>
            {displayEmail && (
              <p className="truncate text-mini text-cloverhound-gray-600">
                {displayEmail}
              </p>
            )}
          </div>

          {userOrganizations.length > 1 && (
            <>
              <Menu.Label>Organizations</Menu.Label>
              {userOrganizations.map((o) => {
                const isCurrent = currentOrg?.id === o.id;
                return (
                  <Menu.Item
                    key={o.id}
                    render={
                      <Link
                        href={`/organizations/${o.slug}/switch`}
                        method="post"
                        as="button"
                      />
                    }
                  >
                    <GlyphTile label={o.name} size="sm" />
                    <span className="truncate">{o.name}</span>
                    {isCurrent && (
                      <Check
                        className="ml-auto size-3.5 text-cloverhound-gray-700 dark:text-cloverhound-gray-300"
                        aria-hidden="true"
                      />
                    )}
                  </Menu.Item>
                );
              })}
            </>
          )}

          {currentOrg && (
            <>
              <Menu.Separator />
              <Menu.Item render={<Link href="/settings/profile" />}>
                <UserIcon className="size-3.5" aria-hidden="true" />
                Profile
              </Menu.Item>
              <Menu.Item render={<Link href="/settings/api_keys" />}>
                <Key className="size-3.5" aria-hidden="true" />
                API keys
              </Menu.Item>
              <Menu.Item render={<Link href="/settings/organization" />}>
                <Building2 className="size-3.5" aria-hidden="true" />
                Organization settings
              </Menu.Item>
            </>
          )}

          <Menu.Separator />
          <Menu.Sub>
            <Menu.SubTrigger>
              <ThemeIcon className="size-3.5" aria-hidden="true" />
              Theme
            </Menu.SubTrigger>
            <Menu.SubContent align="start" side="right" sideOffset={4}>
              <Menu.RadioGroup
                value={mode}
                onValueChange={(v) => setMode(v as ThemeMode)}
              >
                <Menu.RadioItem value="light">
                  <Sun className="size-3.5" aria-hidden="true" />
                  Light
                </Menu.RadioItem>
                <Menu.RadioItem value="dark">
                  <Moon className="size-3.5" aria-hidden="true" />
                  Dark
                </Menu.RadioItem>
                <Menu.RadioItem value="system">
                  <Monitor className="size-3.5" aria-hidden="true" />
                  System
                </Menu.RadioItem>
              </Menu.RadioGroup>
            </Menu.SubContent>
          </Menu.Sub>

          <Menu.Separator />
          <Menu.Item
            onClick={handleLogout}
            className="text-cloverhound-red-500 data-highlighted:bg-cloverhound-red-50 dark:data-highlighted:bg-cloverhound-red-500/10"
          >
            <LogOut className="size-3.5" aria-hidden="true" />
            Sign out
          </Menu.Item>
        </Menu.Content>
      </Menu.Root>
    </div>
  );
}
